import _ from 'lodash'

/**
 * Helper service for handling movements.
 *
 * @type {ngService}
 */
export class TmrMovementHelper {
  static ngType = 'service'

  constructor(debounce, TmrToast, Product, TmrOperationHelper) {
    'ngInject'
    Object.assign(this, { debounce, TmrToast, Product, TmrOperationHelper })
  }

  /**
   * Groups read items by unexpected and upc.
   * Used in inbound operations after a status request.
   *
   * @param {object} details the status request response
   * @param {array} antenna.$epcs
   * @param {object} antenna.$itemsByEpc
   * @param {object} antenna.$products
   * @param {Parcel.parcelItems} expectedItems the items from a parcel
   *
   * @returns {array}
   */
  groupitemsByExpectedUpc(
    details,
    { $epcs, $itemsByEpc, $products },
    expectedItems = []
  ) {
    let groups = []
    this.buildExpectedDetectedGroups(
      groups,
      $epcs,
      details,
      $itemsByEpc,
      $products,
      expectedItems
    )
    this.buildMissingGroups(groups, expectedItems)
    groups.sort((a, b) => this.sortGroups(a, b))
    return groups
  }

  buildExpectedDetectedGroups(
    groups,
    $epcs,
    details,
    $itemsByEpc,
    $products,
    expectedItems
  ) {
    // Cycle $epcs instead of $itemsByEpc because they are ordered by last read.
    $epcs.forEach(epc => {
      if (details[epc] !== undefined && $itemsByEpc[epc] !== undefined) {
        // Creates data grouped by unexpected and upc for the table.
        let unexpected = details[epc].unexpected
        let upc = $itemsByEpc[epc].upc
        let group = groups.find(
          group => group.upc == upc && group.unexpected == unexpected
        )

        // New group.
        if (group === undefined) {
          group = this.buildExpectedGroup(
            upc,
            unexpected,
            expectedItems,
            $products[upc]
          )
          groups.push(group)
        }

        // Update group.
        this.updateGroupData(group, details, epc, groups)
      }
    })
  }

  updateGroupData(group, details, epc, groups) {
    if (group.epcs.indexOf(epc) == -1) {
      group.epcs.push(epc)
      group.statusByEpc[epc] = details[epc].status
      group.weight = _.minBy(groups, 'weight').weight - 1 // Lowest weight - 1 = at the top.
    }
  }

  buildExpectedGroup(upc, unexpected, expectedItems, product) {
    let expectedEpcs = unexpected
      ? []
      : expectedItems.filter(item => item.upc == upc).map(({ epc }) => epc)
    return this.buildGroup(
      upc,
      unexpected,
      expectedEpcs,
      expectedEpcs.length,
      product
    )
  }

  buildMissingGroups(groups, expectedItems) {
    // Add missing groups of expected parcel items
    let missingItems = _.groupBy(expectedItems, 'upc')
    for (let upc in missingItems) {
      if (_.find(groups, { upc, unexpected: false }) === undefined) {
        groups.push(this.buildMissingGroup(upc, missingItems))
      }
    }
    return groups
  }

  buildMissingGroup(upc, missingItems) {
    let expectedEpcs = missingItems[upc].map(({ epc }) => epc)
    return this.buildGroup(
      upc,
      false,
      expectedEpcs,
      expectedEpcs.length,
      this.Product.create(missingItems[upc][0].product)
    )
  }

  buildGroup(upc, unexpected, expectedEpcs, expectedEpcsQty, product) {
    let group = {
      expectedEpcs,
      expectedEpcsQty,
      upc,
      unexpected,
      // Use the corresponding product in the antenna because its product is already loading/loaded.
      product,
      epcs: [],
      statusByEpc: {},
      weight: 0,
    }
    return group
  }

  /**
   * Groups read items by upc and status.
   * Used in transfer operations after a status request.
   *
   * @param {object} details the status request response
   * @param {Antenna} antenna the antenna used in the operation
   *
   * @returns {array}
   */
  groupByUpcStatus(details, antenna) {
    let groupedItems = []
    // Cycle $epcs instead of $itemsByEpc because they are ordered by last read.
    // @todo Build group then sort it.
    antenna.$epcs.forEach(epc => {
      if (
        details[epc] !== undefined &&
        antenna.$itemsByEpc[epc] !== undefined
      ) {
        let item = antenna.$itemsByEpc[epc]
        // Creates data grouped by upc + status for the table.
        let groupIdx = groupedItems.findIndex(
          ({ upc, status }) => upc == item.upc && status == details[epc].status
        )
        let group = groupedItems[groupIdx]

        // New group.
        if (group === undefined) {
          group = {
            upc: item.upc,
            status: details[epc].status,
            // Use the corresponding item in the antenna because its product is already loading/loaded.
            item: antenna.$items.find(item => item.epc == epc),
            epcs: [],
          }
        }
        // Existing group.
        // Remove group so it's re-added to the top of the list
        // (Easier and faster than re-sorting later).
        else groupedItems.splice(groupIdx, 1)

        if (group.epcs.indexOf(epc) == -1) group.epcs.push(epc)

        // Add new groups to top.
        groupedItems.unshift(group)
      }
    })
    return groupedItems
  }

  /**
   * Callback for sorting epc groups by unexpected and last added.
   */
  sortGroups(a, b) {
    // Unexpected are always before expected.
    if (a.unexpected && !b.unexpected) return -1
    if (!a.unexpected && b.unexpected) return 1
    // Lower weight is before higher weight.
    return a.weight - b.weight
  }

  /**
   * Counts items of a list of groups and re-groups them by status.
   */
  countItems(groups) {
    return {
      expected: this.countExpected(groups),
      expected_detected: this.countExpectedDetected(groups),
      unexpected: this.countUnexpected(groups),
    }
  }

  /**
   * Counts expected items of a list of groups
   */
  countExpected(groups) {
    return _.reduce(
      groups,
      (memo, group) => memo + (group.unexpected ? 0 : group.expectedEpcsQty),
      0
    )
  }

  /**
   * Counts expected and detected items of a list of groups
   */
  countExpectedDetected(groups) {
    return _.reduce(
      groups,
      (memo, group) =>
        memo +
        (group.unexpected
          ? 0
          : group.epcs.filter(epc => group.expectedEpcs.indexOf(epc) > -1)
              .length),
      0
    )
  }

  /**
   * Counts unexpected items of a list of groups
   */
  countUnexpected(group) {
    return _.reduce(
      group,
      (memo, group) => memo + (group.unexpected ? group.epcs.length : 0),
      0
    )
  }

  /**
   * Movement status request error callback.
   */
  statusError(error, antenna, onClear) {
    this.TmrToast.exception(error)
    antenna.$stop()
    onClear()
    throw error
  }

  isChecklistConfirmable(groups) {
    if (this.TmrOperationHelper.getConfig('checklistValidationEnable')) {
      return !this.hasUnexpected(groups) && this.hasExpectedUnexpectedOk(groups)
    }
    return true
  }

  hasUnexpected(groups) {
    if (
      this.TmrOperationHelper.getConfig('checklistValidationCheckUnexpected')
    ) {
      return groups.length && groups.findIndex(group => group.unexpected) > -1
    }
    return false
  }

  hasExpectedUnexpectedOk(groups) {
    if (
      this.TmrOperationHelper.getConfig(
        'checklistValidationCheckExpectedUnexpected'
      )
    ) {
      let expected = groups.filter(group => !group.unexpected)
      return _.reduce(
        expected,
        (memo, group) => memo && _.isEqual(group.expectedEpcs, group.epcs),
        true
      )
    }
    return true
  }
}
